import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { bootstrapApi, BootstrapResponse } from '../api/bootstrap';
import { API_BASE_URL } from '../api/client';
import { useAuth } from '../auth/AuthContext';

function bootstrapErrorMessage(err: unknown): string {
    if (err instanceof AxiosError) {
        const base = (err.config?.baseURL as string | undefined) || API_BASE_URL;
        if (err.response) {
            const data = err.response.data as { detail?: string } | undefined;
            if (typeof data?.detail === 'string') {
                return data.detail;
            }
            return `Request failed (${err.response.status}).`;
        }
        if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
            return `Cannot reach the API (${base}). If this is production, set REACT_APP_API_URL to your backend URL and redeploy.`;
        }
        return err.message || 'Failed to load workspace';
    }
    if (err && typeof err === 'object' && 'message' in err && typeof (err as Error).message === 'string') {
        return (err as Error).message;
    }
    return 'Failed to load workspace';
}

type BootstrapState = {
    bootstrap: BootstrapResponse | null;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<BootstrapResponse | null>;
    setActiveDomain: (key: string) => Promise<void>;
    updateProfile: (payload: { display_name?: string; timezone?: string; avatar_url?: string; zip_code?: string }) => Promise<void>;
    completeOnboarding: (payload?: { onboarding_version?: number }) => Promise<void>;
};

const AppBootstrapContext = createContext<BootstrapState | undefined>(undefined);

export const AppBootstrapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { session } = useAuth();
    const [bootstrap, setBootstrap] = useState<BootstrapResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** Same user + token refresh → silent bootstrap refetch without full-screen loading in RouteGuards */
    const bootstrapUserIdRef = useRef<string | null>(null);

    const refresh = useCallback(async (): Promise<BootstrapResponse | null> => {
        if (!session) {
            bootstrapUserIdRef.current = null;
            setBootstrap(null);
            setError(null);
            setLoading(false);
            return null;
        }
        const uid = session.user?.id != null ? String(session.user.id) : null;
        const silent = uid !== null && uid === bootstrapUserIdRef.current;
        if (!silent) {
            setLoading(true);
        }
        setError(null);
        try {
            const data = await bootstrapApi.getBootstrap(session.access_token);
            setBootstrap(data);
            bootstrapUserIdRef.current = uid;
            return data;
        } catch (e: unknown) {
            setError(bootstrapErrorMessage(e));
            return null;
        } finally {
            setLoading(false);
        }
    }, [session]);

    const setActiveDomain = useCallback(async (key: string) => {
        await bootstrapApi.updatePreferences({ active_domain_key: key });
        await refresh();
    }, [refresh]);

    const updateProfile = useCallback(async (payload: { display_name?: string; timezone?: string; avatar_url?: string; zip_code?: string }) => {
        await bootstrapApi.updateProfile(payload);
        await refresh();
    }, [refresh]);

    const completeOnboarding = useCallback(async (payload: { onboarding_version?: number } = {}) => {
        await bootstrapApi.completeOnboarding(payload);
        await refresh();
    }, [refresh]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const value: BootstrapState = {
        bootstrap,
        loading,
        error,
        refresh,
        setActiveDomain,
        updateProfile,
        completeOnboarding,
    };

    return <AppBootstrapContext.Provider value={value}>{children}</AppBootstrapContext.Provider>;
};

export const useAppBootstrap = () => {
    const ctx = useContext(AppBootstrapContext);
    if (!ctx) throw new Error('useAppBootstrap must be used within AppBootstrapProvider');
    return ctx;
};
